import { type TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { compareDesc } from 'date-fns';
import type { CSSProperties } from 'react';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Switcher } from '@sorare/core/src/atoms/navigation/Switcher';
import { WithScrollButtons } from '@sorare/core/src/atoms/navigation/WithScrollButtons';
import { BodyM, BrandXXL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  AUCTION_MARKET_URL,
  INSTANT_BUY_URL,
} from '@sorare/core/src/constants/routes';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useMainColor } from '@sorare/core/src/hooks/useMainColor';
import { isLightColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  desktopAndAbove,
  laptopAndAbove,
  mediumDesktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type { PromoBanners_PromotionalEvent } from './__generated__/index.graphql';
import bg from './assets/bg.webp';
import footballCards from './assets/football-cards.webp';
import nbaCards from './assets/nba-cards.webp';

const StyledFullViewportScroll = styled(FullViewportScroll)`
  background: url(${bg}) top center;
  background-size: cover;
  container-type: inline-size;
`;

const Container = styled(Horizontal)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: var(--double-unit) 0;
  min-height: 300px;

  --card-width: 0;
  @container ${tabletAndAbove} {
    min-height: 360px;
    --card-width: 340px;
    padding: var(--quadruple-unit) 0;
  }
  @container ${laptopAndAbove} {
    --card-width: 480px;
  }
  @container ${desktopAndAbove} {
    --card-width: 540px;
    min-height: 420px;
  }

  @container ${mediumDesktopAndAbove} {
    --card-width: 720px;
    justify-content: space-around;
  }
`;

const LeftSide = styled(Vertical).attrs({ gap: 3 })`
  position: relative;
  isolation: isolate;
  max-width: calc(100% - var(--card-width));
  height: 100%;
  justify-content: center;
`;

const CornerTopLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  @media ${mediumDesktopAndAbove} {
    position: static;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: var(--double-unit);
`;

const Cards = styled(Vertical)`
  --side-image: url('');
  width: var(--card-width);
  height: 100%;
  background: var(--side-image) no-repeat center center;
  background-size: contain;
`;

const Logo = styled.img`
  width: auto;
  height: calc(7 * var(--unit));
`;

const CARDS_IMAGES: Record<Sport, string> = {
  [Sport.FOOTBALL]: footballCards,
  [Sport.NBA]: nbaCards,
  [Sport.BASEBALL]: '',
};

type Props = {
  promotionalEvents: PromoBanners_PromotionalEvent[];
  sport: Sport;
};

export const PromoBanners = ({ promotionalEvents, sport }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    flags: { useMarketHomePromoBanners = false },
  } = useFeatureFlags();
  const promotionalEventsWithBanner = useMemo(
    () =>
      promotionalEvents
        .filter(p => p.pictureUrl)
        .sort((a, b) => compareDesc(a.startDate, b.startDate)),
    [promotionalEvents]
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedPromotionalEvent =
    promotionalEventsWithBanner[activeIndex] || {};
  const mainColor = useMainColor(
    selectedPromotionalEvent?.pictureUrl ?? undefined
  );

  const tab =
    ('hasInstantBuy' in selectedPromotionalEvent &&
      selectedPromotionalEvent.hasInstantBuy) ||
    isType(selectedPromotionalEvent, 'CartPurchaseRewardPromotionalEvent')
      ? INSTANT_BUY_URL[sport]
      : AUCTION_MARKET_URL[sport];

  if (promotionalEventsWithBanner.length === 0 || !useMarketHomePromoBanners) {
    return null;
  }

  return (
    <WithScrollButtons
      wrapperRef={wrapperRef}
      scrollContainerRef={scrollContainerRef}
    >
      <StyledFullViewportScroll
        ref={scrollContainerRef}
        // FIXME WE NEED A DEDICATED IMAGE FOR THIS
        // style={{ background: `url(${selectedPromotionalEvent.pictureUrl})` }}
      >
        <Container>
          <LeftSide>
            <CornerTopLeft>
              <Chip color={isLightColor(mainColor) ? 'black' : 'yellow'}>
                <Horizontal>
                  <FontAwesomeIcon icon={faClock} />
                  <TimeLeft time={selectedPromotionalEvent.endDate} />
                </Horizontal>
              </Chip>
            </CornerTopLeft>

            <Vertical gap={2}>
              {selectedPromotionalEvent.logoUrl ? (
                <Logo src={selectedPromotionalEvent.logoUrl} />
              ) : (
                <BrandXXL>{selectedPromotionalEvent.title}</BrandXXL>
              )}
              <BodyM color="var(--c-nd-600)">
                {selectedPromotionalEvent.description}
              </BodyM>
              <Actions>
                <Button
                  size="medium"
                  color="primary"
                  to={generatePath(
                    `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${selectedPromotionalEvent.slug}#market`
                  )}
                >
                  <FormattedMessage
                    id="PromoBanners.cta"
                    defaultMessage="Shop now"
                  />
                </Button>
                {!!selectedPromotionalEvent.blogUrl && (
                  <Button
                    size="medium"
                    color="secondary"
                    to={selectedPromotionalEvent.blogUrl}
                  >
                    <FormattedMessage {...glossary.learnMore} />
                  </Button>
                )}
              </Actions>
            </Vertical>
            <Switcher
              numberOfItems={promotionalEventsWithBanner.length}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
            />
          </LeftSide>
          <Cards
            style={
              {
                '--side-image': `url("${CARDS_IMAGES[sport]}")`,
              } as CSSProperties
            }
          />
        </Container>
      </StyledFullViewportScroll>
    </WithScrollButtons>
  );
};

PromoBanners.fragments = {
  promotionalEvent: gql`
    fragment PromoBanners_PromotionalEvent on PromotionalEventInterface {
      slug
      title
      description
      startDate
      endDate
      blogUrl
      logoUrl
      pictureUrl
    }
  ` as TypedDocumentNode<PromoBanners_PromotionalEvent>,
};
